import React, { useState, useEffect } from 'react';
import { DocumentHead } from '../components/DocumentHead';
import TextInput from '../components/inputs/TextInput';
import TextArea from '../components/inputs/TextArea';
import { submitSiteContactMessage } from '../lib/services/contact';
import { useSession } from '../lib/auth';

const ContactUsPage: React.FC = () => {
  const { isAuthenticated } = useSession();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{ name?: string; email?: string; message?: string }>(
    {}
  );
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState<Record<string, unknown>>({});

  useEffect(() => {
    // Collect debug information when debug mode is enabled
    if (debugMode) {
      const browserInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        vendor: navigator.vendor,
        cookiesEnabled: navigator.cookieEnabled,
        connectionType:
          'connection' in navigator && navigator.connection
            ? (navigator.connection as { effectiveType?: string }).effectiveType || 'unknown'
            : 'unknown',
        onLine: navigator.onLine,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
      };

      setDebugInfo({
        ...browserInfo,
        supabaseConnected: isAuthenticated,
        session: isAuthenticated ? 'exists' : 'none',
      });
    }
  }, [debugMode, isAuthenticated]);

  const validateForm = () => {
    const errors: { name?: string; email?: string; message?: string } = {};
    let isValid = true;

    if (!name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Invalid email address';
      isValid = false;
    }

    if (!message.trim()) {
      errors.message = 'Message is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const appendDebugInfo = (msg: string) => {
    if (debugMode) {
      return `${msg}\n\n--- DEBUG INFO ---\n${JSON.stringify(debugInfo, null, 2)}`;
    }
    return msg;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const finalMessage = debugMode ? appendDebugInfo(message.trim()) : message.trim();

      await submitSiteContactMessage(name, email, finalMessage);

      setSubmitted(true);

      // Reset form after submission
      setTimeout(() => {
        setName('');
        setEmail('');
        setMessage('');
        setSubmitted(false);
        setFormErrors({});
      }, 5000);
    } catch (err) {
      console.error('Exception:', err);
      setError('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const successMessageStyle = {
    backgroundColor: 'var(--bg-raised)',
    padding: 'var(--space-4)',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-default)',
    maxWidth: '600px',
    marginTop: 'var(--space-4)',
    marginBottom: 'var(--space-10)',
  };

  const errorMessageStyle = {
    backgroundColor: 'var(--bg-raised)',
    padding: 'var(--space-4)',
    borderRadius: '8px',
    borderLeft: '4px solid var(--glyph-critical)',
    boxShadow: 'var(--shadow-default)',
    maxWidth: '600px',
    marginTop: 'var(--space-4)',
    marginBottom: 'var(--space-4)',
  };

  const formStyle = {
    width: '100%',
    maxWidth: '600px',
    marginBottom: 'var(--space-10)',
  };

  const subheadingStyle = {
    marginTop: 'var(--space-4)',
    marginBottom: 'var(--space-10)',
    maxWidth: '600px',
  };

  const buttonsRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--space-4)',
  };

  const checkboxContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: '4px',
  };

  const checkboxStyle = {
    margin: 0,
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  };

  const checkboxLabelStyle = {
    marginLeft: '8px',
    fontSize: 'var(--font-size-body)',
    color: 'var(--glyph-primary)',
    cursor: 'pointer',
  };

  const debugInfoStyle = {
    marginTop: 'var(--space-4)',
    padding: 'var(--space-4)',
    backgroundColor: 'var(--bg-raised)',
    borderRadius: '8px',
    borderLeft: '4px solid var(--glyph-secondary)',
    fontSize: 'var(--font-size-small)',
  };

  const debugInfoPreStyle = {
    maxHeight: '200px',
    overflow: 'auto',
    backgroundColor: 'var(--bg-subtle)',
    padding: 'var(--space-2)',
    borderRadius: '4px',
    marginTop: 'var(--space-2)',
    fontFamily: 'monospace',
    fontSize: 'var(--font-size-small)',
    color: 'var(--glyph-primary)',
  };

  return (
    <div className="contact-page">
      <DocumentHead
        title="Atrivial: Contact Us"
        description="Get in touch with the Atrivial team."
      />

      <section className="relative flex min-h-screen-safe flex-col items-center justify-center px-4 py-6">
        <div className="mx-auto flex w-full max-w-container flex-col">
          <div className="mb-4 flex w-full flex-col items-start gap-4">
            <h1>Contact Us</h1>
            <p style={subheadingStyle}>
              Have an idea? Found a bug? Just want to conenct? We'd love to hear from you!
            </p>
          </div>

          {submitted ? (
            <div style={successMessageStyle}>
              <h2>Thank you for your message!</h2>
              <p>We'll get back to you as soon as possible.</p>
            </div>
          ) : (
            <form style={formStyle} onSubmit={handleSubmit}>
              <TextInput
                id="name"
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required={true}
                error={formErrors.name}
              />

              <TextInput
                id="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required={true}
                error={formErrors.email}
              />

              <TextArea
                id="message"
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                required={true}
                error={formErrors.message}
              />

              {error && (
                <div style={errorMessageStyle}>
                  <p style={{ color: 'var(--glyph-critical)', margin: 0 }}>{error}</p>
                </div>
              )}

              <div style={buttonsRowStyle}>
                <button
                  type="submit"
                  className="inline-flex h-12 min-w-12 cursor-pointer touch-manipulation items-center justify-center gap-2 rounded-md border border-outline-default bg-raised px-4 text-body leading-none text-glyph-primary shadow-default transition-all duration-fast ease-smooth hover:border-outline-hover hover:bg-raised-hover hover:shadow-default-hover disabled:cursor-default disabled:bg-raised/60 disabled:text-glyph-primary/60"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>

                <label style={checkboxContainerStyle}>
                  <input
                    type="checkbox"
                    id="debugToggle"
                    checked={debugMode}
                    onChange={() => setDebugMode(!debugMode)}
                    style={checkboxStyle}
                    aria-label="Include debugging info"
                  />
                  <span style={checkboxLabelStyle}>Include debugging info</span>
                </label>
              </div>

              {debugMode && (
                <div style={debugInfoStyle}>
                  <p>Technical details will be included with your message.</p>
                  <pre style={debugInfoPreStyle}>{JSON.stringify(debugInfo, null, 2)}</pre>
                </div>
              )}
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContactUsPage;
